import { useState } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  useAccommodationPriceRules,
  useUpsertPriceRule,
  useDeletePriceRule,
  type AccommodationPriceRule,
} from '@/hooks/useProducerAccommodations';
import { formatOreToKr } from '@/lib/accommodation/pricing';

interface PriceRuleFormState {
  id?: string;
  rule_type: 'season' | 'special';
  title: string;
  start_date: string;
  end_date: string;
  price_ore: string; // input as kr string
  is_active: boolean;
}

const emptyForm: PriceRuleFormState = {
  rule_type: 'season',
  title: '',
  start_date: '',
  end_date: '',
  price_ore: '',
  is_active: true,
};

function RuleCard({
  rule,
  onEdit,
  onDelete,
}: {
  rule: AccommodationPriceRule;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const startLabel = format(new Date(rule.start_date + 'T00:00:00'), 'd MMM yyyy', { locale: sv });
  const endLabel = format(new Date(rule.end_date + 'T00:00:00'), 'd MMM yyyy', { locale: sv });

  return (
    <div className="flex items-center justify-between rounded-md border p-3 text-sm">
      <div className="space-y-0.5 min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium text-foreground">{rule.title}</span>
          <Badge variant={rule.rule_type === 'special' ? 'default' : 'secondary'} className="text-xs">
            {rule.rule_type === 'special' ? 'Specialpris' : 'Säsongspris'}
          </Badge>
          {!rule.is_active && (
            <Badge variant="outline" className="text-xs">Inaktiv</Badge>
          )}
        </div>
        <p className="text-muted-foreground">
          {startLabel} – {endLabel} · <span className="font-medium text-foreground">{formatOreToKr(rule.price_ore)} kr / natt</span>
        </p>
      </div>
      <div className="flex items-center gap-1 shrink-0 ml-2">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onEdit}>
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={onDelete}>
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

interface AccommodationPricingSectionProps {
  accommodationId: string;
  basePriceText: string;
  onBasePriceChange: (value: string) => void;
}

export function AccommodationPricingSection({
  accommodationId,
  basePriceText,
  onBasePriceChange,
}: AccommodationPricingSectionProps) {
  const { data: rules = [] } = useAccommodationPriceRules(accommodationId);
  const upsert = useUpsertPriceRule();
  const deleteRule = useDeletePriceRule();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [formState, setFormState] = useState<PriceRuleFormState>(emptyForm);

  const seasonRules = rules.filter(r => r.rule_type === 'season');
  const specialRules = rules.filter(r => r.rule_type === 'special');

  const openNew = (type: 'season' | 'special') => {
    setFormState({ ...emptyForm, rule_type: type });
    setDialogOpen(true);
  };

  const openEdit = (rule: AccommodationPriceRule) => {
    setFormState({
      id: rule.id,
      rule_type: rule.rule_type as 'season' | 'special',
      title: rule.title,
      start_date: rule.start_date,
      end_date: rule.end_date,
      price_ore: String(Math.round(rule.price_ore / 100)),
      is_active: rule.is_active,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (rule: AccommodationPriceRule) => {
    try {
      await deleteRule.mutateAsync({ ruleId: rule.id, accommodationId });
      toast.success('Prisregel borttagen');
    } catch {
      toast.error('Kunde inte ta bort prisregel');
    }
  };

  const handleSave = async () => {
    if (!formState.title.trim()) {
      toast.error('Ange ett namn');
      return;
    }
    if (!formState.start_date || !formState.end_date) {
      toast.error('Ange start- och slutdatum');
      return;
    }
    if (formState.end_date < formState.start_date) {
      toast.error('Slutdatum måste vara efter startdatum');
      return;
    }
    const priceKr = parseInt(formState.price_ore);
    if (!priceKr || priceKr <= 0) {
      toast.error('Ange ett giltigt pris');
      return;
    }

    // Check for same-type overlap
    const sameTypeRules = rules.filter(
      r => r.rule_type === formState.rule_type && r.id !== formState.id && r.is_active,
    );
    const hasOverlap = sameTypeRules.some(
      r => formState.start_date <= r.end_date && formState.end_date >= r.start_date,
    );
    if (hasOverlap) {
      toast.error(
        formState.rule_type === 'season'
          ? 'Datumen överlappar ett befintligt säsongspris'
          : 'Datumen överlappar ett befintligt specialpris',
      );
      return;
    }

    try {
      await upsert.mutateAsync({
        ...(formState.id ? { id: formState.id } : {}),
        accommodation_id: accommodationId,
        rule_type: formState.rule_type,
        title: formState.title.trim(),
        start_date: formState.start_date,
        end_date: formState.end_date,
        price_ore: priceKr * 100,
        is_active: formState.is_active,
      });
      toast.success(formState.id ? 'Prisregel uppdaterad' : 'Prisregel tillagd');
      setDialogOpen(false);
    } catch {
      toast.error('Kunde inte spara prisregel');
    }
  };

  // Format the base price preview
  const basePricePreview = (() => {
    const trimmed = basePriceText.trim();
    if (!trimmed) return null;
    if (/kr|per|natt/i.test(trimmed)) return trimmed;
    if (/^\d+$/.test(trimmed)) return `Från ${trimmed} kr per natt`;
    return trimmed;
  })();

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Priser</CardTitle>
          <p className="text-sm text-muted-foreground">Baspris, säsongspriser och specialpriser</p>
        </CardHeader>
        <CardContent className="space-y-8">
          {/* Base price */}
          <div className="space-y-2">
            <Label htmlFor="base-price" className="text-sm font-medium">Baspris per natt</Label>
            <Input
              id="base-price"
              value={basePriceText}
              onChange={e => onBasePriceChange(e.target.value)}
              placeholder="t.ex. 800"
            />
            {basePricePreview && (
              <p className="text-xs text-muted-foreground">
                Visas som: "{basePricePreview}"
              </p>
            )}
          </div>

          {/* Season rules */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium text-foreground">Säsongspriser</h4>
                <p className="text-xs text-muted-foreground">Priser som gäller under specifika perioder</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => openNew('season')}>
                <Plus className="h-3.5 w-3.5 mr-1" />
                Lägg till
              </Button>
            </div>
            {seasonRules.length > 0 ? (
              <div className="space-y-2">
                {seasonRules.map(r => (
                  <RuleCard key={r.id} rule={r} onEdit={() => openEdit(r)} onDelete={() => handleDelete(r)} />
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground italic">Inga säsongspriser ännu</p>
            )}
          </div>

          {/* Special rules */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium text-foreground">Specialpriser</h4>
                <p className="text-xs text-muted-foreground">Helger, högtider och andra undantag. Specialpris har högst prioritet.</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => openNew('special')}>
                <Plus className="h-3.5 w-3.5 mr-1" />
                Lägg till
              </Button>
            </div>
            {specialRules.length > 0 ? (
              <div className="space-y-2">
                {specialRules.map(r => (
                  <RuleCard key={r.id} rule={r} onEdit={() => openEdit(r)} onDelete={() => handleDelete(r)} />
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground italic">Inga specialpriser ännu</p>
            )}
          </div>

          {/* Priority explanation */}
          {rules.length > 0 && (
            <p className="text-xs text-muted-foreground border-t pt-3">
              Prioritet: Specialpris → Säsongspris → Baspris. Om flera regler matchar ett datum gäller den med högst prioritet.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Rule form dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {formState.id ? 'Redigera prisregel' : formState.rule_type === 'special' ? 'Nytt specialpris' : 'Nytt säsongspris'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Namn *</Label>
              <Input
                value={formState.title}
                onChange={e => setFormState(prev => ({ ...prev, title: e.target.value }))}
                placeholder={formState.rule_type === 'special' ? 't.ex. Midsommar' : 't.ex. Högsäsong'}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>Typ</Label>
              <Select
                value={formState.rule_type}
                onValueChange={(v: 'season' | 'special') => setFormState(prev => ({ ...prev, rule_type: v }))}
              >
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="season">Säsongspris</SelectItem>
                  <SelectItem value="special">Specialpris</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Gäller från *</Label>
                <Input
                  type="date"
                  value={formState.start_date}
                  onChange={e => setFormState(prev => ({ ...prev, start_date: e.target.value }))}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Gäller till *</Label>
                <Input
                  type="date"
                  value={formState.end_date}
                  onChange={e => setFormState(prev => ({ ...prev, end_date: e.target.value }))}
                  className="mt-1.5"
                />
              </div>
            </div>
            <div>
              <Label>Pris per natt (kr) *</Label>
              <Input
                type="number"
                min={0}
                value={formState.price_ore}
                onChange={e => setFormState(prev => ({ ...prev, price_ore: e.target.value }))}
                placeholder="t.ex. 1200"
                className="mt-1.5"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={formState.is_active}
                onCheckedChange={v => setFormState(prev => ({ ...prev, is_active: v }))}
              />
              <Label>Aktiv</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Avbryt</Button>
            <Button onClick={handleSave} disabled={upsert.isPending}>
              {upsert.isPending ? 'Sparar...' : formState.id ? 'Uppdatera' : 'Lägg till'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
